import { Card, Space, Typography, Badge, Tooltip, Button } from 'antd'
import {
  InstagramOutlined,
  FacebookOutlined,
  TwitterOutlined,
  YoutubeOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  LinkOutlined,
} from '@ant-design/icons'
import { Link } from 'react-router-dom'

const { Text } = Typography

export type SocialPlatform = 'INSTAGRAM' | 'FACEBOOK' | 'TIKTOK' | 'TWITTER' | 'YOUTUBE'

interface ConnectedAccount {
  platform: SocialPlatform
  accountName: string
  accountHandle?: string
  profileImageUrl?: string
  status: 'ACTIVE' | 'EXPIRED' | 'REVOKED' | 'ERROR'
}

interface PlatformSelectorProps {
  value?: SocialPlatform[]
  onChange?: (platforms: SocialPlatform[]) => void
  connectedAccounts?: ConnectedAccount[]
  characterCount?: number
}

interface PlatformConfig {
  icon: React.ReactNode
  name: string
  color: string
  bgColor: string
  charLimit: number
  videoDuration: number // seconds
}

const platformConfig: Record<SocialPlatform, PlatformConfig> = {
  INSTAGRAM: {
    icon: <InstagramOutlined />,
    name: 'Instagram',
    color: '#E4405F',
    bgColor: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)',
    charLimit: 2200,
    videoDuration: 90,
  },
  FACEBOOK: {
    icon: <FacebookOutlined />,
    name: 'Facebook',
    color: '#1877F2',
    bgColor: '#1877F2',
    charLimit: 63206,
    videoDuration: 240,
  },
  TIKTOK: {
    icon: <span style={{ fontWeight: 'bold' }}>TT</span>,
    name: 'TikTok',
    color: '#000000',
    bgColor: '#000000',
    charLimit: 2200,
    videoDuration: 180,
  },
  TWITTER: {
    icon: <TwitterOutlined />,
    name: 'X',
    color: '#000000',
    bgColor: '#000000',
    charLimit: 280,
    videoDuration: 140,
  },
  YOUTUBE: {
    icon: <YoutubeOutlined />,
    name: 'YouTube',
    color: '#FF0000',
    bgColor: '#FF0000',
    charLimit: 5000,
    videoDuration: 60,
  },
}

const allPlatforms: SocialPlatform[] = ['INSTAGRAM', 'FACEBOOK', 'TIKTOK', 'TWITTER', 'YOUTUBE']

export function PlatformSelector({
  value = [],
  onChange,
  connectedAccounts = [],
  characterCount = 0,
}: PlatformSelectorProps) {
  const togglePlatform = (platform: SocialPlatform) => {
    const isConnected = connectedAccounts.some(
      (a) => a.platform === platform && a.status === 'ACTIVE'
    )

    if (!isConnected) return // Can't select if not connected

    const newValue = value.includes(platform)
      ? value.filter((p) => p !== platform)
      : [...value, platform]

    onChange?.(newValue)
  }

  return (
    <Card title="Platforme" size="small">
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        {allPlatforms.map((platform) => {
          const config = platformConfig[platform]
          const account = connectedAccounts.find((a) => a.platform === platform)
          const isConnected = account?.status === 'ACTIVE'
          const isSelected = value.includes(platform)
          const isOverLimit = characterCount > config.charLimit

          return (
            <div
              key={platform}
              onClick={() => togglePlatform(platform)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                borderRadius: 8,
                border: `2px solid ${isSelected ? config.color : '#f0f0f0'}`,
                backgroundColor: isSelected ? `${config.color}10` : 'white',
                cursor: isConnected ? 'pointer' : 'not-allowed',
                opacity: isConnected ? 1 : 0.5,
                transition: 'all 0.2s',
              }}
            >
              <Space>
                {/* Platform icon */}
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    background: config.bgColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: 18,
                  }}
                >
                  {config.icon}
                </div>

                <div>
                  <Text strong>{config.name}</Text>
                  {isConnected && account ? (
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        @{account.accountHandle || account.accountName}
                      </Text>
                    </div>
                  ) : (
                    <div>
                      <Link
                        to="/social-media/accounts"
                        onClick={(e) => e.stopPropagation()}
                        style={{ fontSize: 12 }}
                      >
                        <LinkOutlined /> Povezi
                      </Link>
                    </div>
                  )}
                </div>
              </Space>

              <Space>
                {/* Character limit indicator */}
                {isSelected && (
                  <Tooltip title={`Max ${config.charLimit} karaktera`}>
                    <Text
                      type={isOverLimit ? 'danger' : 'secondary'}
                      style={{ fontSize: 12 }}
                    >
                      {characterCount}/{config.charLimit}
                    </Text>
                  </Tooltip>
                )}

                {/* Connection status */}
                {isConnected ? (
                  <Badge status="success" />
                ) : account?.status === 'EXPIRED' ? (
                  <Tooltip title="Token istekao">
                    <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                  </Tooltip>
                ) : account?.status === 'ERROR' ? (
                  <Tooltip title="Greska">
                    <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                  </Tooltip>
                ) : null}

                {/* Selection indicator */}
                {isSelected && (
                  <CheckCircleOutlined style={{ color: config.color, fontSize: 18 }} />
                )}
              </Space>
            </div>
          )
        })}

        {value.length === 0 && (
          <Text type="secondary" style={{ textAlign: 'center', display: 'block', marginTop: 8 }}>
            Izaberite bar jednu platformu
          </Text>
        )}
      </Space>
    </Card>
  )
}

export default PlatformSelector
